<template>
    <div class="chart-wrapper">
        <div id="chart"></div>
        <template v-if="withCanvas">
            <div class="canvas-wrap">
                <canvas id="chart-canvas" :width="this.svgWidth" :height="this.maxHeight"></canvas>
            </div>
            <p class="submit" v-if="withDownload">
                <label>File Download Name</label>
                <input v-model="dlName" />
                <button class="button button-primary" @click.prevent="download">Download</button>
            </p>
        </template>
    </div>
</template>
<script>
import tippy from 'tippy.js';
import * as d3 from 'd3';
import { fabric } from "fabric";
fabric.Object.prototype.objectCaching = false;

export default {
    props: {
        selected: {
            type: Array,
            default: () => []
        },
        name: {
            type: String,
            default: null
        },
        chartData: {
            type: Object,
            default: () => rankingChart.chartData
        },
        topics: {
            type: Array,
            default: () => rankingChart.topics
        },
        withComments: {
            type: Boolean,
            default: true
        },
        withCanvas: {
            type: Boolean,
            default: false
        },
        withDownload: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        quarters() {
            return Object.keys( this.chartData );
        },
        chartItems() {
            return this.topics.map(topic => ({
                ...topic,
                plot: this.quarters.map(quarter => {
                    const cData = this.chartData[quarter];
                    const rank = cData.findIndex(c => c.topic_id === topic.topic_id);
                    
                    return {
                        rank,
                        comment: cData[rank] ? cData[rank].comment: null
                    };
                })
            }));
        },
        maxHeight() {
            return 410;
        },
        headerHeight() {
            return 15;
        },
        columnWidth() {
            return 125;
        },
        inactiveFill() {
            return '#BEBBBD';
        },
        rowHeight() {
            return Math.floor(( this.maxHeight - this.headerHeight ) / 10);
        },
        itemPaddingH() {
            return 25;
        },
        itemPaddingV() {
            return 7.5;
        },
        itemWidth() {
            return this.columnWidth - (this.itemPaddingH * 2);
        },
        lineHLength() {
            return 10;
        },
        itemHeight() {
            return this.rowHeight - (this.itemPaddingV * 2);
        },
        svgWidth() {
            return this.columnWidth * this.quarters.length;
        },
    },
    data() {
        const sanitizedName = this.name !== null ? this.name.replace(/[^a-zA-Z0-9]/g,'') : null;

        return {
            svg: null,
            canvas: null,
            dlName: sanitizedName
        }
    },
    watch: {
        chartData() {
            this.$el.querySelector('#chart svg').remove();
            this.svg = null;
            this.initSvg();
            this.toggleSelected();
            setTimeout(() => this.setCanvas(), 1000);
        }
    },
    mounted() {
        this.initSvg();
        this.initToggleSelected();
        setTimeout(() => this.setCanvas(), 1000);
    },
    methods: {
        setCanvas() {
            if( this.withCanvas ) {
                var s = new XMLSerializer().serializeToString(this.$el.querySelector('#chart svg'));
                var encodedData = window.btoa(s);
                let url = `data:image/svg+xml;base64,${encodedData}`;

                if( this.canvas === null ) {
                    this.canvas = new fabric.Canvas('chart-canvas', {
                        renderOnAddRemove: true,
                        enableRetinaScaling: false,
                        backgroundColor: '#ffffff'
                    });

                    this.canvas.selection = false;
                    this.canvas.setWidth(this.svgWidth);
                    this.canvas.setHeight(this.maxHeight);
                } else {
                    this.canvas.clear();
                }

                fabric.loadSVGFromURL( url, objects => {
                    objects.forEach(obj => {
                        if( obj.type === 'text' ) {
                            obj.top = obj.top + 2.95;
                        }

                        obj.selectable = false;
                        this.canvas.add(obj);
                    });

                    this.canvas.setWidth(this.svgWidth);
                    this.canvas.setHeight(this.maxHeight);
                    this.canvas.requestRenderAll();
                });
            }
        },
        initSvg() {
            this.svg = d3.select(this.$el.querySelector('#chart'))
                .append('svg')
                    .attr('width',this.svgWidth)
                    .attr('height',this.maxHeight)
                    .attr('viewBox',`0 0 ${this.svgWidth} ${this.maxHeight}`)
                    .attr('style','background-color:#ffffff')
                    .attr('x',0)
                    .attr('y',0)
                    .attr('preserveAspectRatio','xMidYMin slice')
                    .attr('version',"1.1")
                    .attr('xmlns',"http://www.w3.org/2000/svg")
                    .attr('xmlns:xlink',"http://www.w3.org/1999/xlink")
                    .attr('xml:space',"preserve");

            // headers
            const headerGroup = this.svg.append('g');

            this.quarters.forEach((q,idx) => {
                headerGroup.append('text')
                    .attr('font-family','GreycliffCF')
                    .attr('font-size',10)
                    .attr('font-wight','bold')
                    .attr('text-anchor','middle')
                    .attr('alignment-baseline','central')
                    .attr('x',this.itemX(idx) + (this.columnWidth / 2))
                    .attr('y',this.headerHeight/2)
                    .text(q);
            });

            // items
            const itemGroups = this.svg.append('g').attr('transform',`translate(${0},${this.headerHeight*2})`);

            // console.log(this.chartItems);

            this.chartItems.forEach(item => {
                const hasPlot = typeof item.plot.find(p => p.rank > -1) !== 'undefined';

                if( hasPlot ) {
                    const itemGroup = itemGroups.append('g')
                        .attr('data-item-group',item.topic_id);

                    item.plot.forEach((plot,idx) => {
                        
                        if( plot.rank > -1 ) {
                            if( idx > 0 ) {
                                const prevIdx = idx - 1;
                                const prevRank = item.plot[prevIdx].rank;
                                const currRank = plot.rank;

                                if( prevRank > -1 ) {
                                    let pathStr;
                                
                                    if( prevRank === currRank ) {
                                        const sx = this.itemX(prevIdx) + this.itemWidth + this.itemPaddingH;
                                        const ex = this.itemX(idx) + this.itemPaddingH;
                                        const sy = this.itemY(prevRank) + (this.itemHeight/2);
                                        pathStr = `M${sx},${sy} L${ex},${sy}`;
                                    } else {
                                        const sx = this.itemX(prevIdx) + this.itemWidth + this.itemPaddingH;
                                        const sy = this.itemY(prevRank) + (this.itemHeight/2);
                                        const stop1x = sx + this.lineHLength;
                                        const stop2x = sx + ( this.itemPaddingH * 2 ) - this.lineHLength;
                                        const stop2y = this.itemY(currRank) + (this.itemHeight/2);
                                        const ex = stop2x + this.lineHLength;
                                        
                                        pathStr = `M${sx},${sy} L${stop1x},${sy} L${stop2x},${stop2y} L${ex},${stop2y}`;
                                    }

                                    if( pathStr ) {
                                        const connector = itemGroup.append('path')
                                            .attr('d',pathStr)
                                            .attr('fill','transparent')
                                            .attr('stroke', this.inactiveFill)
                                            .attr('stroke-width', 5)
                                            .attr('data-item','fillable')
                                            .attr('stroke-linejoin', 'round');
                                    }

                                }
                            }

                            const ig = itemGroup.append('g')
                                .attr('data-rank-item','')
                                .attr('transform',`translate(${this.itemX(idx)},${this.itemY(plot.rank)})`);
                                
                            const rect = ig.append('rect')
                                .attr('rx',10)
                                .attr('ry',10)
                                .attr('fill',this.inactiveFill)
                                .attr('width',this.itemWidth)
                                .attr('height',this.itemHeight)
                                .attr('x',this.itemPaddingH)
                                .attr('data-item','fillable');

                            const text = ig.append('text')
                                .attr('font-family','GreycliffCF')
                                .attr('font-size',8)
                                .attr('text-anchor','middle')
                                .attr('alignment-baseline','central')
                                .attr('x',(this.columnWidth / 2))
                                .attr('y',this.itemHeight/2)
                                .text(item.topic_name)
                                .style('fill','#ffffff');

                            let comment = false;

                            if( this.withComments && plot.comment !== '' && plot.comment !== null ) {
                                comment = true;

                                const cg = ig.append('g')
                                    .attr('transform',`translate(${this.columnWidth - this.itemPaddingH},0)`)
                                    .attr('data-tippy-content', plot.comment)
                                    .attr('data-item','comment');

                                const cgCircle = cg.append('circle')
                                    .attr('r',6)
                                    .attr('fill','#EC1C24')
                                    .attr('width',14)
                                    .attr('height',14)
                                    .attr('stroke','#ffffff')
                                    .attr('stroke-width',1);

                                const cgText = cg.append('text')
                                    .attr('font-family','GreycliffCF')
                                    .attr('font-size',7)
                                    .attr('text-anchor','middle')
                                    .attr('alignment-baseline','central')
                                    .text('i')
                                    .style('fill','#ffffff');

                                tippy( cg.nodes(), {
                                    theme: 'gray',
                                    placement: 'auto',
                                    appendTo: document.getElementById('interactive-chart'),
                                    arrow: true,
                                    trigger: 'click',
                                    interactive: true,
                                    zIndex: 3,
                                    popperOptions: {
                                        placements: 'auto'
                                    }
                                });
                            }
                        }
                    });
                }
            });

        },
        initToggleSelected() {
            this.$parent.$on('selection', e => this.toggleSelected( e ) );
        },
        toggleSelected( selected = [] ) {
            if( selected.length === 0 ) selected = this.selected;

            const items = [ ...this.$el.querySelectorAll('[data-item-group]') ];

            items.forEach(item => {
                const id = item.getAttribute('data-item-group');
                const itemObj = this.topics.find(i => i.topic_id === id);
                const shouldBeSelected = selected.includes(id);
                const isCurrentlySelected = item.classList.contains('active');

                const fillables = [ ...item.querySelectorAll('[data-item=fillable]') ];

                if( !isCurrentlySelected && shouldBeSelected ) {
                    item.classList.add('active');

                    fillables.forEach(f => {
                        if( f.tagName === 'path' ) {
                            f.setAttribute('stroke', itemObj.color);
                        } else {
                            f.setAttribute('fill', itemObj.color);
                        }
                    });
                } else if( isCurrentlySelected && !shouldBeSelected ) {
                    item.classList.remove('active');

                    fillables.forEach(f => {
                        if( f.tagName === 'path' ) {
                            f.setAttribute('stroke', this.inactiveFill);
                        } else {
                            f.setAttribute('fill', this.inactiveFill);
                        }
                    });
                }
                
            });
        },
        itemX( idx ) {
            return ( idx * this.columnWidth );
        },
        itemY( rank ) {
            return ( this.rowHeight * rank );
        },
        download() {
            if( this.canvas ) {
                const dataUrl = this.canvas.toDataURL({ format: 'jpeg', quality: 1 });
                const link = document.createElement('a');
                const dStr = new Date().getTime();
                link.setAttribute('href',dataUrl);
                link.setAttribute('download',`${this.dlName}-${dStr}.jpg`);
                link.click();
                link.remove();
            }
        }
    }
}
</script>